import { useQuery } from "@tanstack/react-query";
import { fetchGroupMembers, type GroupMembersResponse } from "@/api/nurses";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

/**
 * 근무자관리 월 셀렉터 — 선택 월 '소속' 멤버 + 헤드카운트 (Phase 4).
 *
 * 백엔드: GET /nurses/members?group_id&year&month
 * - '소속'(근무일 수 아님) 기준 명단 + 상태(active/inbound/outbound/dispatch_out/leave/resigned)
 *   + 마커(←/→) + 배지(파견 중/휴직). 영구 전출은 그 달 미표시.
 * - isAdmin 게이팅 없이 selectedGroupId 그대로 전달(백엔드 resolve_effective_group 가
 *   홈/original/관리그룹 권한 검증). 과거 isAdmin 게이팅이 그룹 조회 버그 원인이었음.
 */
export default function useGroupMembers(
  groupId?: string | null,
  year?: number,
  month?: number,
  options?: { enabled?: boolean }
) {
  return useQuery<GroupMembersResponse>({
    queryKey: QUERY_KEYS.nurses.members(groupId, year, month),
    queryFn: async () => {
      const result = await fetchGroupMembers(
        groupId as string,
        year as number,
        month as number
      );
      if (import.meta.env.DEV) {
        const resignationRows = result.members
          .filter(
            (member) =>
              member.membership_status === "resigned" ||
              member.badge === "퇴사" ||
              member.resign_date != null
          )
          .map((member) => ({
            nurseId: member.nurse_id,
            name: member.name,
            membershipStatus: member.membership_status,
            badge: member.badge,
            resignDate: member.resign_date,
            resignReason: member.resign_reason,
          }));
        console.info("[resignation-debug] GET /nurses/members", {
          groupId,
          year,
          month,
          resignationRows,
        });
      }
      return result;
    },
    enabled: (options?.enabled ?? true) && !!groupId && !!year && !!month,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    retry: 1,
    retryDelay: 1000,
  });
}
